import { Button } from "@components/ui/button";
import { Calendar } from "@components/ui/calendar";
import { Input } from "@components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { CalendarIcon } from "lucide-react";
import React, { useState, useEffect } from "react";

function formatDate(date: Date | undefined) {
  if (!date) {
    return "";
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

interface InputDatePickerProps extends Omit<React.ComponentProps<"input">, "onChange"> {
  onChange?: (date: Date) => void;
  value?: string;
  minDate?: Date;
}

export default function InputDatePicker({
  onChange,
  value,
  minDate,
  disabled,
  ...props
}: InputDatePickerProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(value ? new Date(value as string) : undefined);
  const [month, setMonth] = useState<Date | undefined>(date);
  const [inputValue, setInputValue] = useState(formatDate(date));
  const [error, setError] = useState<string | null>(null);

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      const newDate = new Date(value as string);
      if (isValidDate(newDate)) {
        setDate(newDate);
        setInputValue(formatDate(newDate));
        setError(null);
      }
    } else {
      // Clear the date when value is undefined/null
      setDate(undefined);
      setInputValue("");
      setError(null);
    }
  }, [value]);

  return (
    <div className="relative">
      <Input
        {...props}
        disabled={disabled}
        value={inputValue}
        data-invalid={!!error}
        className={error ? "border-red-500" : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          setInputValue(raw);
          const newDate = new Date(raw);
          if (isValidDate(newDate)) {
            if (minDate) {
              const min = new Date(minDate);
              min.setHours(0, 0, 0, 0);
              const candidate = new Date(newDate);
              candidate.setHours(0, 0, 0, 0);
              if (candidate < min) {
                setError("Date cannot be in the past");
                setInputValue(formatDate(date));
                return;
              }
            }
            setError(null);
            setDate(newDate);
            setMonth(newDate);
            onChange?.(newDate as any);
          } else if (raw.trim() === "") {
            setError(null);
          }
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
      <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
        <PopoverTrigger asChild>
          <Button
            id="date-picker"
            variant="ghost"
            disabled={disabled}
            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 disabled:pointer-events-none"
          >
            <CalendarIcon className="size-3.5" />
            <span className="sr-only">Select date</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto overflow-hidden p-0"
          align="end"
          alignOffset={-8}
          sideOffset={10}
        >
          <Calendar
            mode="single"
            selected={date}
            captionLayout="dropdown"
            month={month}
            onMonthChange={setMonth}
            disabled={minDate ? { before: minDate } : undefined}
            onSelect={(selectedDate) => {
              if (selectedDate) {
                setDate(selectedDate);
                setInputValue(formatDate(selectedDate));
                setError(null);
                setOpen(false);
                onChange?.(selectedDate as any);
              }
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
